import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';
import { getVisibleCartIcon } from '@theme/fly-to-cart';

/**
 * SOLEYA — Quick Add
 *
 * Root cause of the previous "blank white modal" bug: this component used to
 * fetch the product's PDP URL, look for a `[data-product-grid-content]`
 * element in the response, and morph it into a shared `<quick-add-dialog>`.
 * That element only ever existed in the theme's native
 * `snippets/product-information-content.liquid` template. This store's PDP
 * (`sections/soleya-product.liquid`, wired via `templates/product.json`) is a
 * fully custom section that never renders that markup, so the lookup always
 * failed — yet `#openQuickAddModal()` was called unconditionally regardless
 * of whether any content was found, opening an empty dialog every time.
 *
 * Fix: Quick Add no longer fetches or depends on the PDP at all. Each
 * product card already renders its own lightweight size popover (see
 * `snippets/quick-add.liquid`, `[data-quick-add-size-popover]`) directly from
 * Liquid, using the card's own variant data. `handleClick` now simply toggles
 * that popover open/closed. Selecting a size sets the existing hidden
 * variant-id input and submits the existing `<product-form-component>` via
 * the same proven AJAX add-to-cart path already used by the single-variant
 * "Add" button (`assets/product-form.js`), which itself dispatches
 * `CartAddEvent` and opens the cart drawer — no modal, no page refresh, no
 * confirmation page.
 */
export class QuickAddComponent extends Component {
  /**
   * NOTE: the size popover and its trigger button live inside a nested
   * `<product-form-component>`, which the Component base class treats as its
   * own refs boundary (any element whose tag name ends in "-component" is
   * treated as a scope boundary when resolving `this.refs`). Plain
   * `querySelector` is used here instead of `this.refs` so these elements
   * resolve correctly regardless of that nesting.
   * @returns {HTMLElement | null}
   */
  get #sizePopover() {
    return this.querySelector('[data-quick-add-size-popover]');
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  get #chooseButton() {
    return this.querySelector('.quick-add__button--choose');
  }

  /**
   * @returns {HTMLElement | null}
   */
  get #confirmation() {
    return this.querySelector('[data-quick-add-confirmation]');
  }

  /** @type {number | undefined} */
  #confirmationTimeout;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeSizePopover);
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeSizePopover);
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    document.removeEventListener('pointerdown', this.#handleOutsideInteraction, { capture: true });
    document.removeEventListener('keydown', this.#handleKeydown);

    if (this.#confirmationTimeout) clearTimeout(this.#confirmationTimeout);
  }

  /**
   * Handles a click on the "Choose" quick-add button. Opens/closes the
   * inline size popover rendered on the product card. Never opens a modal.
   * @param {Event} event - The click event.
   */
  handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasAttribute('data-size-popover-open')) {
      this.#closeSizePopover();
    } else {
      this.#openSizePopover();
    }
  };

  /**
   * Handles a click on a size chip inside the popover: sets the variant on
   * the existing product form and submits it via the standard AJAX add-to-cart
   * flow, then closes the popover immediately.
   * @param {MouseEvent} event - The click event.
   */
  handleSizeSelect = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Note: this framework's `on:click` delegation dispatches through a proxied
    // event whose `target` is set to the matched element — the real
    // `currentTarget` at dispatch time is `document`, so `target` must be used.
    const button = /** @type {HTMLElement | null} */ (event.target);
    if (!button || button.hasAttribute('disabled')) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const form = this.querySelector('product-form-component form');
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));

    if (!(form instanceof HTMLFormElement) || !variantInput) return;

    variantInput.value = variantId;
    variantInput.removeAttribute('disabled');

    this.#closeSizePopover();
    this.#playAddedConfirmation(button);

    form.requestSubmit();
  };

  /**
   * Plays the premium "added to bag" confirmation: an optional fly-to-cart
   * animation from the size chip to the cart icon, plus a brief "Added to
   * Bag" toast. The cart badge itself updates independently and instantly
   * via cart-icon.js's own CartAddEvent listener.
   *
   * Uses `getVisibleCartIcon()` (from `@theme/fly-to-cart`) rather than a
   * raw selector so the animation always targets whichever cart icon is
   * actually visible right now — correct whether the header is at full
   * height, sticky/shrunk, or (in a theme layout with more than one
   * candidate element) whichever instance is on screen.
   * @param {HTMLElement} sourceElement - The element the animation flies from.
   */
  #playAddedConfirmation(sourceElement) {
    if (!prefersReducedMotion()) {
      const cartIcon = getVisibleCartIcon();
      const image = this.dataset.productImage;

      if (cartIcon) {
        const flyToCart = document.createElement('fly-to-cart');
        flyToCart.classList.add('fly-to-cart--quick');
        if (image) flyToCart.style.setProperty('background-image', `url(${image})`);
        flyToCart.style.setProperty('--start-opacity', '0');
        flyToCart.source = sourceElement;
        flyToCart.destination = cartIcon;
        document.body.appendChild(flyToCart);
      }
    }

    const confirmation = this.#confirmation;
    if (!confirmation) return;

    if (this.#confirmationTimeout) clearTimeout(this.#confirmationTimeout);

    confirmation.removeAttribute('inert');
    confirmation.setAttribute('aria-hidden', 'false');
    this.setAttribute('data-confirmation-visible', 'true');

    this.#confirmationTimeout = window.setTimeout(() => {
      confirmation.setAttribute('inert', '');
      confirmation.setAttribute('aria-hidden', 'true');
      this.removeAttribute('data-confirmation-visible');
    }, 1000);
  }

  /**
   * Opens the size popover, closing any other open popover first so only one
   * is ever visible at a time.
   */
  #openSizePopover = () => {
    const sizePopover = this.#sizePopover;
    const chooseButton = this.#chooseButton;
    if (!sizePopover) return;

    document
      .querySelectorAll('quick-add-component[data-size-popover-open]')
      .forEach((/** @type {Element} */ openComponent) => {
        if (openComponent instanceof QuickAddComponent && openComponent !== this) {
          openComponent.#closeSizePopover();
        }
      });

    sizePopover.removeAttribute('inert');
    sizePopover.setAttribute('aria-hidden', 'false');
    this.setAttribute('data-size-popover-open', 'true');
    chooseButton?.setAttribute('aria-expanded', 'true');

    document.addEventListener('pointerdown', this.#handleOutsideInteraction, { capture: true });
    document.addEventListener('keydown', this.#handleKeydown);
  };

  /**
   * Closes the size popover.
   */
  #closeSizePopover = () => {
    const sizePopover = this.#sizePopover;
    const chooseButton = this.#chooseButton;
    if (!sizePopover) return;

    sizePopover.setAttribute('inert', '');
    sizePopover.setAttribute('aria-hidden', 'true');
    this.removeAttribute('data-size-popover-open');
    chooseButton?.setAttribute('aria-expanded', 'false');

    document.removeEventListener('pointerdown', this.#handleOutsideInteraction, { capture: true });
    document.removeEventListener('keydown', this.#handleKeydown);
  };

  /**
   * Closes the popover when the user interacts outside of it.
   * @param {PointerEvent} event
   */
  #handleOutsideInteraction = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) {
      this.#closeSizePopover();
    }
  };

  /**
   * Closes the popover on Escape.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#closeSizePopover();
      this.#chooseButton?.focus();
    }
  };

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {import('@theme/events').VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
